import { Avatar, Box, Button, Grid2, Modal } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EmployeeUser } from "../../types";
import QRCode from "react-qr-code";
import { DownloadPDF } from "../DownloadPDF";
import profileGenericPic from "../../assets/img/logos/profileGenericPic.jpg";
import { useAuth } from "../../context/auth-context";
import { COUNTRIES, getCountryCode } from "../../constants/countries";
import { getQrDisplayFlags } from "../../utils/qr-display";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "500px",
  maxWidth: 1200,
  maxHeight: "80vh",
  bgcolor: "#fff",
  border: "1px solid #898989",
  boxShadow: 24,
  borderRadius: "20px",
};

interface QRModalProps {
  employee: EmployeeUser;
}

export function QRModal({ employee }: QRModalProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  const { country } = useAuth();
  const countryCode = getCountryCode(country);
  const countryAllowsTipsQr = countryCode === COUNTRIES.AR;
  const URL = process.env.REACT_APP_LANDING_URL;
  const headerColor = "#EF0BB8"; // Color del header
  const flags = getQrDisplayFlags(employee.qrType, {
    canShowTipQr: countryAllowsTipsQr && employee.qrTipsCode != null,
    hasGoogleLink: Boolean(employee.googleLink?.trim()),
    onlyRatings: employee.onlyRatings,
  });
  const canShowTipQr = flags.showTipQr;
  const showRatingQr = flags.showRatingQr;
  const showGoogleQr = flags.showGoogleQr;
  const showSideQr = showRatingQr || showGoogleQr;
  const showBothQRs = canShowTipQr && showSideQr;
  const sideQrValue = showGoogleQr
    ? employee.googleLink!
    : `${URL}/?userId=${employee.userId}&branchId=${employee.branchId}`;
  const sideQrLabel = showGoogleQr ? t("qr.google") : t("qr.ratings");

  if (!URL) {
    return (
      <Button
        onClick={() => setIsModalOpen(true)}
        title="QR"
        sx={{
          margin: 0,
          minWidth: "fit-content",
          background: "transparent",
          border: "1px solid #EF0BB8",
          borderRadius: "50%",
          color: "#EF0BB8",
          textTransform: "none",
          fontFamily: "Montserrat",
          fontWeight: 300,
          width: "2.5rem",
          height: "2.5rem",
        }}
      >
        <svg
          fill="#EF0BB8"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M5 15v2H3v-2Zm-2 6h2v-2H3Zm2-4v2h2v-2Zm4-6H7v2H5v2h4Zm-6 0v2h2v-2Zm6 8H7v2h5v-2h-1v-2H9Zm4-15h-2v2h2Zm-2 7h2V8h-2ZM4 9a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1Zm1-2h2V5H5Zm16-3v8h-2V9h-3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1Zm-2 1h-2v2h2Zm2 11v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-3h-4v-4h2v2h1v-4h2v4h4a1 1 0 0 1 1 1Zm-2 1h-2v2h2Z" />
        </svg>
      </Button>
    );
  }

  // Validar que employee tenga los campos necesarios
  if (!employee.userId || !employee.branchId) {
    return (
      <Button
        onClick={() => setIsModalOpen(true)}
        title="QR"
        sx={{
          margin: 0,
          minWidth: "fit-content",
          background: "transparent",
          border: "1px solid #EF0BB8",
          borderRadius: "50%",
          color: "#EF0BB8",
          textTransform: "none",
          fontFamily: "Montserrat",
          fontWeight: 300,
          width: "2.5rem",
          height: "2.5rem",
        }}
      >
        <svg
          fill="#EF0BB8"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M5 15v2H3v-2Zm-2 6h2v-2H3Zm2-4v2h2v-2Zm4-6H7v2H5v2h4Zm-6 0v2h2v-2Zm6 8H7v2h5v-2h-1v-2H9Zm4-15h-2v2h2Zm-2 7h2V8h-2ZM4 9a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1Zm1-2h2V5H5Zm16-3v8h-2V9h-3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1Zm-2 1h-2v2h2Zm2 11v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-3h-4v-4h2v2h1v-4h2v4h4a1 1 0 0 1 1 1Zm-2 1h-2v2h2Z" />
        </svg>
      </Button>
    );
  }

  return (
    <>
      <Button
        onClick={() => setIsModalOpen(true)}
        title="QR"
        sx={{
          margin: 0,
          minWidth: "fit-content",
          background: "transparent",
          border: "1px solid #EF0BB8",
          borderRadius: "50%",
          color: "#EF0BB8",
          textTransform: "none",
          fontFamily: "Montserrat",
          fontWeight: 300,
          width: "2.5rem",
          height: "2.5rem",
        }}
      >
        <svg
          fill="#EF0BB8"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M5 15v2H3v-2Zm-2 6h2v-2H3Zm2-4v2h2v-2Zm4-6H7v2H5v2h4Zm-6 0v2h2v-2Zm6 8H7v2h5v-2h-1v-2H9Zm4-15h-2v2h2Zm-2 7h2V8h-2ZM4 9a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1Zm1-2h2V5H5Zm16-3v8h-2V9h-3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1Zm-2 1h-2v2h2Zm2 11v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-3h-4v-4h2v2h1v-4h2v4h4a1 1 0 0 1 1 1Zm-2 1h-2v2h2Z" />
        </svg>
      </Button>
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby={t("teams.group.create.title")}
        aria-describedby={t("teams.group.create.title")}
      >
        <Box sx={style}>
          <Box className="container mx-auto tip-box">
            <Grid2 container className="w-full h-1/5 header">
              <Grid2 size={4} className="avatar">
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    top: "20px",
                    position: "absolute",
                  }}
                  src={employee.imageUrl ?? profileGenericPic}
                  alt="Avatar"
                />
              </Grid2>
              <Grid2 size={8} className="header-name">
                <div className="pl-4 pb-4 flex flex-col">
                  <span className="name leading-5">{employee.name}</span>
                </div>
              </Grid2>
            </Grid2>
            
            {/* QR con formato estilizado */}
            <div className="p-4 h-3/5 w-full flex items-center justify-center">
              {(!URL || !employee.userId || !employee.branchId) ? (
                <div className="text-center text-red-500">
                  <p>Error: No se pueden generar los códigos QR</p>
                  <p className="text-sm mt-2">
                    {!URL && "URL de landing no configurada"}
                    {!employee.userId && "ID de usuario no válido"}
                    {!employee.branchId && "ID de sucursal no válido"}
                  </p>
                </div>
              ) : (
                <div style={{ 
                  position: "relative", 
                  width: showBothQRs ? "400px" : "200px", 
                  minHeight: "250px",
                  display: "flex",
                  flexDirection: "row",
                  gap: "16px",
                  justifyContent: "center"
                }}>
                  {showSideQr && (
                  <div style={{
                    position: "relative", 
                    width: "200px", 
                    minHeight: "250px",
                    border: `1px solid ${headerColor}`,
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxSizing: "border-box"
                  }}>
                    <div style={{
                      position: "absolute", 
                      top: 0, 
                      left: 0, 
                      right: 0, 
                      height: "48px", 
                      backgroundColor: "white", 
                      padding: "8px", 
                      borderRadius: "8px 8px 0 0", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center", 
                      border: `1px solid ${headerColor}`, 
                      borderBottom: `1px solid ${headerColor}`
                    }}>
                      <p style={{
                        fontSize: "12px", 
                        margin: 0, 
                        color: "#575b5b",
                        fontWeight: "500"
                      }}>
                        {sideQrLabel}
                      </p>
                    </div>
                    <div style={{
                      position: "absolute", 
                      top: "48px", 
                      left: 0, 
                      right: 0, 
                      bottom: 0, 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center", 
                      border: `1px solid ${headerColor}`, 
                      borderTop: "none", 
                      borderRadius: "0 0 8px 8px"
                    }}>
                      <div style={{
                        width: "152px", 
                        height: "152px", 
                        marginTop: "8px"
                      }}>
                        <QRCode
                          size={152}
                          style={{ 
                            height: "100%", 
                            maxWidth: "100%", 
                            width: "100%" 
                          }}
                          value={sideQrValue}
                          viewBox={`0 0 152 152`}
                        />
                      </div>
                    </div>
                  </div>
                  )}

                  {/* QR de Propinas - Solo mostrar si qrTipsCode existe y es válido */}
                  {canShowTipQr && employee.qrTipsCode && (
                    <div style={{
                      position: "relative", 
                      width: "200px", 
                      minHeight: "250px"
                    }}>
                      <div style={{
                        position: "relative", 
                        width: "200px", 
                        minHeight: "250px",
                        border: `1px solid ${headerColor}`,
                        borderRadius: "8px",
                        overflow: "hidden",
                        boxSizing: "border-box"
                      }}>
                        <div style={{
                          position: "absolute", 
                          top: 0, 
                          left: 0,
                          right: 0, 
                          height: "48px", 
                          backgroundColor: headerColor, 
                          padding: "8px", 
                          borderRadius: "8px 8px 0 0", 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "center", 
                          border: `1px solid ${headerColor}`, 
                          borderBottom: "none"
                        }}>
                          <p style={{
                            fontSize: "12px", 
                            margin: 0, 
                            color: "white",
                            fontWeight: "500"
                          }}>
                            {t("qr.tips")}
                          </p>
                        </div>
                        <div style={{
                          position: "absolute", 
                          top: "48px", 
                          left: 0, 
                          right: 0, 
                          bottom: 0, 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "center", 
                          border: `1px solid ${headerColor}`, 
                          borderTop: "none", 
                          borderRadius: "0 0 8px 8px"
                        }}>
                          <div style={{
                            width: "152px", 
                            height: "152px", 
                            marginTop: "8px"
                          }}>
                            <QRCode
                              size={152}
                              style={{ 
                                height: "100%", 
                                maxWidth: "100%", 
                                width: "100%" 
                              }}
                              value={employee.qrTipsCode}
                              viewBox={`0 0 152 152`}
                            />
                          </div>
                        </div>
                      </div>
                      <div style={{
                        position: "absolute",
                        bottom: "-20px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "100%",
                        textAlign: "center"
                      }}>
                        <p style={{
                          fontSize: "10px",
                          margin: 0,
                          color: "#EF0BB8",
                          fontWeight: "500"
                        }}>
                          {t("qr.from-wallet")}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-center mt-5 mx-24">
              <DownloadPDF
                variant="mui"
                employeeName={employee.name}
                userId={employee.userId}
                branchId={employee.branchId}
              />
            </div>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
